import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Award, CheckCircle, Percent, GraduationCap, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScholarshipPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const [scholarships, setScholarships] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scholarship_id: '',
    course_id: searchParams.get('course') || '',
    remarks: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [scholarshipsRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/api/scholarships`),
          axios.get(`${API_URL}/api/courses`)
        ]);
        setScholarships(scholarshipsRes.data);
        setCourses(coursesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.info('Please login to apply for scholarship');
      navigate('/login', { state: { from: '/scholarship' } });
      return;
    }

    setLoading(true);
    try {
      // Include snapshot details so admin always sees full info
      const selectedScholarship = displayScholarships.find(s => s.id === formData.scholarship_id);
      const selectedCourse = courses.find(c => c.id === formData.course_id);
      await axios.post(
        `${API_URL}/api/scholarships/apply`,
        {
          student_id: user.id,
          scholarship_id: formData.scholarship_id,
          course_id: formData.course_id,
          documents: [],
          remarks: formData.remarks,
          scholarship_name: selectedScholarship?.name || null,
          discount_percentage: selectedScholarship?.discount_percentage || null,
          course_name: selectedCourse?.name || null,
          course_category: selectedCourse?.category || null,
          course_fee: selectedCourse?.fee || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scholarship application submitted successfully!');
      setFormData({ scholarship_id: '', course_id: '', remarks: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const defaultScholarships = [
    { id: '1', name: 'Merit Scholarship', discount_percentage: 100, eligibility_criteria: 'Top 10 rank in qualifying exam', description: 'Full scholarship for exceptional performers.' },
    { id: '2', name: 'Excellence Award', discount_percentage: 75, eligibility_criteria: 'Top 100 rank or 95%+ in board exams', description: '75% fee waiver for outstanding students.' },
    { id: '3', name: 'Talent Scholarship', discount_percentage: 50, eligibility_criteria: '90%+ in board exams', description: '50% scholarship for talented students.' },
    { id: '4', name: 'Early Bird Discount', discount_percentage: 25, eligibility_criteria: 'Registration before deadline', description: '25% discount for early registrations.' },
  ];

  const displayScholarships = scholarships.length > 0 ? scholarships : defaultScholarships;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-rose-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-secondary text-secondary-foreground">Scholarships</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Financial Aid for <span className="text-primary">Bright Minds</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We believe that financial constraints should never stop a talented student. Explore our scholarship programs offering up to 100% fee waiver.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '₹5Cr+', label: 'Scholarships Awarded' },
              { value: '5000+', label: 'Students Benefited' },
              { value: '100%', label: 'Max Scholarship' },
              { value: '4', label: 'Scholarship Types' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="font-heading text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-white/70">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scholarship Types */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              Available <span className="text-primary">Scholarships</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayScholarships.map((scholarship, index) => (
              <motion.div
                key={scholarship.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full card-hover text-center" data-testid={`scholarship-${scholarship.id}`}>
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 mx-auto rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                      <Percent className="w-8 h-8 text-secondary" />
                    </div>
                    <Badge className="mb-2 bg-primary">{scholarship.discount_percentage}% OFF</Badge>
                    <h3 className="font-heading font-semibold text-lg mb-2">{scholarship.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{scholarship.description}</p>
                    <div className="pt-4 border-t">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">Eligibility:</span> {scholarship.eligibility_criteria}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="shadow-xl" data-testid="scholarship-form">
              <CardHeader className="text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-primary flex items-center justify-center mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="font-heading text-2xl">Apply for Scholarship</CardTitle>
                <CardDescription>
                  Fill out the form below to apply for a scholarship. Our team will review your application.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label>Select Scholarship</Label>
                    <Select
                      value={formData.scholarship_id}
                      onValueChange={(value) => setFormData({ ...formData, scholarship_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="scholarship-select">
                        <SelectValue placeholder="Choose a scholarship" />
                      </SelectTrigger>
                      <SelectContent>
                        {displayScholarships.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name} - {s.discount_percentage}% OFF
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Select Course</Label>
                    <Select
                      value={formData.course_id}
                      onValueChange={(value) => setFormData({ ...formData, course_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="course-select">
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} - {c.category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Additional Information</Label>
                    <Textarea
                      placeholder="Tell us why you deserve this scholarship..."
                      rows={4}
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                      data-testid="scholarship-remarks"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="scholarship-submit">
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </Button>

                  {!isAuthenticated && (
                    <p className="text-sm text-center text-muted-foreground">
                      You need to <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/login')}>login</Button> to apply for scholarship.
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold">
              How It <span className="text-primary">Works</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: FileText, title: 'Apply', description: 'Fill out the scholarship application form' },
              { icon: GraduationCap, title: 'Review', description: 'Our team reviews your application' },
              { icon: CheckCircle, title: 'Approval', description: 'Get notified about your scholarship status' },
              { icon: Award, title: 'Enroll', description: 'Complete enrollment with discounted fee' },
            ].map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4 relative">
                  <step.icon className="w-8 h-8 text-primary" />
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="font-heading font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ScholarshipPage;
